import type { Directive, DirectiveBinding } from 'vue';

import { computePosition, flip, shift, offset, autoUpdate, Placement } from '@floating-ui/dom';

type TooltipValue = string | { message: string; position?: string };

interface TooltipState {
    tooltip: HTMLElement | null;
    cleanup: (() => void) | null;
    showHandler: () => void;
    hideHandler: () => void;
    placement: Placement;
    content: string;
}

// Map directive positions to Floating UI placements
const positionMap: Record<string, Placement> = {
    bottom: 'bottom',
    top: 'top',
    'bottom-left': 'bottom-start',
    'bottom-right': 'bottom-end',
    'top-left': 'top-start',
    'top-right': 'top-end',
    left: 'left',
    right: 'right',
};

// Track tooltip state per element
const tooltipRegistry = new WeakMap<HTMLElement, TooltipState>();

// Track all active tooltip states for global cleanup
const activeTooltips = new Set<TooltipState>();

// Cooldown after hiding to prevent immediate re-show
const COOLDOWN_MS = 150;
let lastHideTime = 0;

// Hide all tooltips (used during navigation, modal opens, etc.)
function hideAllTooltips(): void {
    lastHideTime = Date.now();
    // Remove all tooltip elements from DOM directly
    document.querySelectorAll('.tooltip').forEach((el) => el.remove());

    // Clear all active tooltip states
    activeTooltips.forEach((state) => {
        if (state.cleanup) {
            state.cleanup();
            state.cleanup = null;
        }
        state.tooltip = null;
    });
    activeTooltips.clear();
}

// Setup global listeners to hide tooltips
if (typeof document !== 'undefined') {
    // Hide on Inertia navigation
    document.addEventListener('inertia:before', hideAllTooltips);

    // Hide on any click (modal opens, navigation, etc.)
    document.addEventListener('pointerdown', hideAllTooltips, true);

    // Hide when window loses focus
    window.addEventListener('blur', hideAllTooltips);

    // Hide when tab becomes hidden
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            hideAllTooltips();
        }
    });
}

function getPlacement(value: TooltipValue): Placement {
    const position = typeof value === 'string' ? 'bottom-left' : value.position || 'bottom-left';
    return positionMap[position] || 'bottom-start';
}

function getContent(value: TooltipValue): string {
    return typeof value === 'string' ? value : value.message;
}

function showTooltip(el: HTMLElement, state: TooltipState): void {
    // Skip if no content
    if (!state.content) {
        return;
    }

    // Skip if within cooldown period (prevents flicker after click)
    if (Date.now() - lastHideTime < COOLDOWN_MS) {
        return;
    }

    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.classList.add('tooltip');
    tooltip.setAttribute('data-placement', state.placement);
    // Note: Using innerHTML to preserve backward compatibility with existing HTML tooltips.
    // Tooltip content comes from developer code, not user input.
    tooltip.innerHTML = state.content;
    state.tooltip = tooltip;

    // Append to body
    document.body.appendChild(tooltip);

    // Position the tooltip using Floating UI
    const updatePosition = (): void => {
        if (!state.tooltip) {
            return;
        }

        computePosition(el, state.tooltip, {
            placement: state.placement,
            strategy: 'fixed',
            middleware: [offset(6), flip(), shift({ padding: 5 })],
        }).then(({ x, y, placement }) => {
            if (!state.tooltip) {
                return;
            }

            Object.assign(state.tooltip.style, {
                left: `${x}px`,
                top: `${y}px`,
            });

            // Update data-placement for arrow styling
            state.tooltip.setAttribute('data-placement', placement);
        });
    };

    // Initial position
    updatePosition();

    // Auto-update on scroll/resize
    state.cleanup = autoUpdate(el, tooltip, updatePosition);

    // Track as active tooltip
    activeTooltips.add(state);

    // Show with slight delay for fade-in
    requestAnimationFrame(() => {
        if (state.tooltip) {
            state.tooltip.classList.add('tooltip--visible');
        }
    });
}

function hideTooltip(state: TooltipState): void {
    // Remove from active tooltips
    activeTooltips.delete(state);

    // Cleanup auto-update
    if (state.cleanup) {
        state.cleanup();
        state.cleanup = null;
    }

    // Remove tooltip from DOM
    if (state.tooltip) {
        state.tooltip.remove();
        state.tooltip = null;
    }
}

export const vTooltip: Directive<HTMLElement, TooltipValue> = {
    mounted(el: HTMLElement, binding: DirectiveBinding<TooltipValue>) {
        if (typeof binding.value === 'undefined') {
            return;
        }

        // Empty content still registers: dynamic tooltips may start empty and
        // receive text later via updated() (showTooltip skips empty content).
        const content = getContent(binding.value);

        const state: TooltipState = {
            tooltip: null,
            cleanup: null,
            placement: getPlacement(binding.value),
            content,
            showHandler: () => showTooltip(el, state),
            hideHandler: () => hideTooltip(state),
        };

        tooltipRegistry.set(el, state);

        // Add event listeners
        el.addEventListener('mouseenter', state.showHandler);
        el.addEventListener('mouseleave', state.hideHandler);
        el.addEventListener('focusin', state.showHandler);
        el.addEventListener('focusout', state.hideHandler);
    },

    updated(el: HTMLElement, binding: DirectiveBinding<TooltipValue>) {
        const state = tooltipRegistry.get(el);
        if (!state) {
            return;
        }

        // Update content and placement
        state.content = getContent(binding.value);
        state.placement = getPlacement(binding.value);

        // If tooltip is currently visible, update it
        if (state.tooltip) {
            // Note: Using innerHTML to preserve backward compatibility
            state.tooltip.innerHTML = state.content;
            state.tooltip.setAttribute('data-placement', state.placement);
        }
    },

    unmounted(el: HTMLElement) {
        const state = tooltipRegistry.get(el);
        if (!state) {
            return;
        }

        // Remove event listeners
        el.removeEventListener('mouseenter', state.showHandler);
        el.removeEventListener('mouseleave', state.hideHandler);
        el.removeEventListener('focusin', state.showHandler);
        el.removeEventListener('focusout', state.hideHandler);

        // Cleanup tooltip
        hideTooltip(state);

        // Remove from registry
        tooltipRegistry.delete(el);
    },
};
